import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import {
    CloudRain,
    Heart,
    HeartHandshake,
    Mail,
    MailOpen,
    Moon,
    Smile,
    Sparkles,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLockScroll } from '@/hooks/use-lock-scroll'
import { OPEN_WHEN } from '@/constants/birthday'

const ICONS = [Heart, CloudRain, Smile, Moon, HeartHandshake, Sparkles]

const OpenWhen: FC = (): ReactNode => {
    const [active, setActive] = useState<number | null>(null)
    const [opened, setOpened] = useState<boolean[]>(() => OPEN_WHEN.letters.map(() => false))
    const dialogRef = useRef<HTMLDialogElement>(null)
    const letter = active === null ? null : OPEN_WHEN.letters[active]
    const ActiveIcon = active === null ? Heart : ICONS[active]

    useLockScroll(active !== null)

    useEffect(() => {
        const dialog = dialogRef.current

        if (!dialog) return

        if (active !== null && !dialog.open) dialog.showModal()
        if (active === null && dialog.open) dialog.close()
    }, [active])

    const openLetter = (index: number): void => {
        setActive(index)
        setOpened((current) => current.map((value, letterIndex) => letterIndex === index ? true : value))
    }

    return (
        <div className='rounded-[2rem] border border-primary/10 bg-white p-6 shadow-xl shadow-primary/5 sm:p-10'>
            <div className='mx-auto max-w-xl text-center'>
                <h3 className='font-heading text-3xl font-semibold'>
                    {OPEN_WHEN.title}
                </h3>

                <p className='mt-3 text-muted-foreground text-pretty'>
                    {OPEN_WHEN.subtitle}
                </p>
            </div>

            <ul className='mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                {OPEN_WHEN.letters.map((item, index) => {
                    const Icon = ICONS[index]
                    const wasOpened = opened[index]

                    return (
                        <li key={item.title}>
                            <div className='group relative flex h-full flex-col items-center overflow-hidden rounded-3xl bg-secondary/70 px-5 pb-6 pt-12 text-center transition hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/10 motion-reduce:transition-none'>
                                <span
                                    aria-hidden
                                    className={cn(
                                        'absolute inset-x-0 top-0 h-16 origin-top bg-primary/15 transition-transform duration-500 [clip-path:polygon(0_0,100%_0,50%_100%)] motion-reduce:transition-none',
                                        wasOpened && 'scale-y-0'
                                    )}
                                />

                                <span className='relative grid size-12 place-items-center rounded-full bg-white text-primary shadow-md'>
                                    <Icon className='size-5' />
                                </span>

                                <p className='mt-4 font-heading text-xl font-semibold text-balance'>
                                    {item.title}
                                </p>

                                <div className='mt-auto pt-5'>
                                    <button
                                        type='button'
                                        onClick={() => openLetter(index)}
                                        className={cn(
                                            'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30',
                                            wasOpened
                                                ? 'border border-primary/25 text-primary hover:bg-white'
                                                : 'bg-primary text-primary-foreground shadow-md shadow-primary/25 hover:brightness-110'
                                        )}
                                    >
                                        {wasOpened ? <MailOpen className='size-4' /> : <Mail className='size-4' />}
                                        {wasOpened ? OPEN_WHEN.reopen : OPEN_WHEN.open}
                                    </button>
                                </div>
                            </div>
                        </li>
                    )
                })}
            </ul>

            <dialog
                ref={dialogRef}
                aria-labelledby='open-when-title'
                onClose={() => setActive(null)}
                onClick={(event) => {
                    if (event.target === event.currentTarget) setActive(null)
                }}
                className='m-auto max-h-[88vh] w-[min(92vw,32rem)] max-w-none overflow-y-auto rounded-[2rem] border-0 bg-paper p-0 text-foreground shadow-2xl backdrop:bg-wine/70 backdrop:backdrop-blur-sm open:animate-in open:fade-in open:zoom-in-95 open:duration-300'
            >
                {letter && (
                    <div className='relative px-7 pb-10 pt-10 sm:px-10'>
                        <button
                            type='button'
                            onClick={() => setActive(null)}
                            aria-label={OPEN_WHEN.close}
                            className='absolute right-4 top-4 grid size-10 place-items-center rounded-full text-muted-foreground transition hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                        >
                            <X className='size-5' />
                        </button>

                        <span className='grid size-12 place-items-center rounded-full bg-primary/10 text-primary'>
                            <ActiveIcon className='size-6' />
                        </span>

                        <h4 id='open-when-title' className='mt-5 pr-8 font-heading text-2xl font-semibold'>
                            {letter.title}
                        </h4>

                        <p className='paper-lines mt-5 font-hand text-2xl leading-9'>
                            {letter.message}
                        </p>

                        <Heart aria-hidden className='ml-auto mt-6 size-6 fill-primary text-primary' />
                    </div>
                )}
            </dialog>
        </div>
    )
}

export default OpenWhen
